import React, { useCallback, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { mergeAttributes, ResizableNodeView } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import FontFamily from "@tiptap/extension-font-family";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import CodeIcon from "@mui/icons-material/Code";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import SubscriptIcon from "@mui/icons-material/Subscript";
import SuperscriptIcon from "@mui/icons-material/Superscript";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import FontDownloadOutlinedIcon from "@mui/icons-material/FontDownloadOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import NotesIcon from "@mui/icons-material/Notes";
import TitleIcon from "@mui/icons-material/Title";
import LinkIcon from "@mui/icons-material/Link";
import ImageIcon from "@mui/icons-material/Image";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { toast } from "react-toastify";
import { IMAGE_BASE } from "../../utils/constants";
import { mediaUploadApi } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import "./TipTapEditor.css";

const RESIZE_OPTIONS = {
  enabled: true,
  // Corners = diagonal; edges = horizontal / vertical
  directions: [
    "top",
    "bottom",
    "left",
    "right",
    "top-left",
    "top-right",
    "bottom-left",
    "bottom-right",
  ],
  minWidth: 80,
  minHeight: 80,
  // Free resize; hold Shift while dragging to lock aspect ratio
  alwaysPreserveAspectRatio: false,
};

const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class"),
        renderHTML: (attributes) => {
          if (!attributes.class) return {};
          return { class: attributes.class };
        },
      },
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style"),
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
      width: {
        default: null,
        parseHTML: (element) => {
          const width = element.getAttribute("width") || element.style.width;
          return width ? parseInt(width, 10) || null : null;
        },
        renderHTML: (attributes) => {
          if (!attributes.width) return {};
          const height = attributes.height
            ? `height: ${attributes.height}px;`
            : "height: auto;";
          return {
            width: attributes.width,
            style: `width: ${attributes.width}px; ${height}`,
          };
        },
      },
      height: {
        default: null,
        parseHTML: (element) => {
          const height = element.getAttribute("height") || element.style.height;
          return height ? parseInt(height, 10) || null : null;
        },
        renderHTML: (attributes) => {
          if (!attributes.height) return {};
          return { height: attributes.height };
        },
      },
    };
  },

  addNodeView() {
    if (!this.options.resize?.enabled || typeof document === "undefined") {
      return null;
    }

    const { directions, minWidth, minHeight, alwaysPreserveAspectRatio } =
      this.options.resize;
    const extension = this;

    return ({ node, getPos, HTMLAttributes, editor }) => {
      const el = document.createElement("img");
      // Keep native drag enabled — this is how the user repositions the
      // image anywhere in the text (ProseMirror moves the node on drop).
      el.draggable = true;

      const mergedAttributes = mergeAttributes(
        extension.options.HTMLAttributes,
        HTMLAttributes
      );

      Object.entries(mergedAttributes).forEach(([key, value]) => {
        if (value == null) return;
        if (key === "width" || key === "height") return;
        el.setAttribute(key, value);
      });

      if (mergedAttributes.src != null) {
        el.src = mergedAttributes.src;
      }

      const nodeView = new ResizableNodeView({
        element: el,
        editor,
        node,
        getPos,
        onResize: (width, height) => {
          el.style.width = `${width}px`;
          el.style.height = `${height}px`;
          // The container's own width is fixed at mount time. Without
          // updating it live too, the img's `max-width: 100%` resolves
          // against that stale (smaller) container and clamps the visible
          // size straight back down, making the drag look like it does
          // nothing — especially noticeable dragging wider.
          nodeView.dom.style.width = `${width}px`;
        },
        onCommit: (width, height) => {
          const pos = getPos();
          if (pos === undefined) return;
          nodeView.dom.style.width = `${width}px`;
          editor
            .chain()
            .setNodeSelection(pos)
            .updateAttributes(extension.name, { width, height })
            .run();
        },
        onUpdate: (updatedNode) => {
          if (updatedNode.type !== node.type) return false;
          if (updatedNode.attrs.src !== el.getAttribute("src")) {
            el.src = updatedNode.attrs.src;
          }
          if (updatedNode.attrs.class != null) {
            el.className = updatedNode.attrs.class;
          }
          syncFloatToContainer(nodeView.dom, updatedNode.attrs.class);
          if (updatedNode.attrs.width) {
            el.style.width = `${updatedNode.attrs.width}px`;
            nodeView.dom.style.width = `${updatedNode.attrs.width}px`;
          }
          if (updatedNode.attrs.height) {
            el.style.height = `${updatedNode.attrs.height}px`;
          }
          return true;
        },
        options: {
          directions,
          min: { width: minWidth, height: minHeight },
          preserveAspectRatio: alwaysPreserveAspectRatio === true,
          className: {
            container: "tiptap-resize-container",
            wrapper: "tiptap-resize-wrapper",
            handle: "tiptap-resize-handle",
            resizing: "is-resizing",
          },
        },
      });

      const syncFloatToContainer = (container, className) => {
        const floatClasses = [
          "float-left",
          "float-right",
          "align-center",
          "align-left",
          "align-right",
        ];
        floatClasses.forEach((c) => container.classList.remove(c));
        String(className || "")
          .split(/\s+/)
          .filter(Boolean)
          .forEach((c) => {
            if (floatClasses.includes(c)) container.classList.add(c);
          });
      };

      syncFloatToContainer(nodeView.dom, node.attrs.class);
      if (node.attrs.width) {
        nodeView.dom.style.width = `${node.attrs.width}px`;
      }

      // Avoid TipTap's onload race: cached images never fire onload after src is set
      const reveal = () => {
        nodeView.dom.style.visibility = "";
        nodeView.dom.style.pointerEvents = "";
      };

      nodeView.dom.style.visibility = "hidden";
      nodeView.dom.style.pointerEvents = "none";

      if (el.complete && el.naturalWidth > 0) {
        reveal();
      } else {
        el.addEventListener("load", reveal, { once: true });
        el.addEventListener("error", reveal, { once: true });
      }

      return nodeView;
    };
  },
}).configure({
  allowBase64: false,
  resize: RESIZE_OPTIONS,
  // Inline lets the image sit inside a paragraph's text flow (like Word's
  // "in line with text"), instead of always forcing its own block/line.
  inline: true,
  HTMLAttributes: {
    class: "tiptap-image",
  },
});

const HEADING_LEVELS = [1, 2, 3, 4];

const FONT_FAMILIES = [
  { label: "Default", value: null },
  { label: "Serif", value: "Georgia, 'Times New Roman', serif" },
  { label: "Sans Serif", value: "Arial, Helvetica, sans-serif" },
  { label: "Monospace", value: "'Courier New', Courier, monospace" },
  { label: "Cursive", value: "'Brush Script MT', cursive" },
];

const TEXT_COLORS = [
  "#212B36",
  "#637381",
  "#D32F2F",
  "#E65100",
  "#F9A825",
  "#2E7D32",
  "#0277BD",
  "#2065D1",
  "#6A1B9A",
  "#AD1457",
];

const HIGHLIGHT_COLORS = [
  "#FFF59D",
  "#FFCC80",
  "#A5D6A7",
  "#90CAF9",
  "#CE93D8",
  "#EF9A9A",
  "#B0BEC5",
];

const ColorSwatchButton = ({ title, icon, colors, onPick, onClear, activeColor }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  return (
    <>
      <ToolbarButton title={title} onClick={(e) => setAnchorEl(e.currentTarget)} active={!!activeColor}>
        {icon}
      </ToolbarButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack direction="row" flexWrap="wrap" sx={{ p: 1, gap: 0.75, maxWidth: 168 }}>
          {colors.map((color) => (
            <Box
              key={color}
              data-color-swatch={color}
              onClick={() => {
                onPick(color);
                setAnchorEl(null);
              }}
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: color,
                cursor: "pointer",
                border: "1px solid rgba(0,0,0,0.15)",
                outline: activeColor === color ? "2px solid #2065d1" : "none",
                outlineOffset: 1,
              }}
            />
          ))}
          <Box
            onClick={() => {
              onClear();
              setAnchorEl(null);
            }}
            title="Clear"
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              border: "1px solid rgba(0,0,0,0.15)",
              cursor: "pointer",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                left: "50%",
                top: "50%",
                width: "140%",
                height: 2,
                bgcolor: "#D32F2F",
                transform: "translate(-50%, -50%) rotate(45deg)",
              },
            }}
          />
        </Stack>
      </Popover>
    </>
  );
};

const resolveUploadedUrl = (payload) => {
  // Axios response body: { status, message, data: { path, url } }
  const outer = payload?.data ?? payload;
  const data = outer?.data && typeof outer.data === "object" ? outer.data : outer;
  const candidate =
    data?.url ||
    data?.path ||
    data?.image ||
    data?.fileName ||
    (typeof outer?.data === "string" ? outer.data : null) ||
    (typeof data === "string" ? data : null);

  if (!candidate) return null;
  if (candidate.startsWith("http") || candidate.startsWith("data:")) return candidate;
  const base = (IMAGE_BASE || "").replace(/\/$/, "");
  const path = String(candidate).replace(/\\/g, "/").replace(/^\//, "");
  return `${base}/${path}`;
};

const ToolbarButton = ({ title, onClick, active, disabled, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        color={active ? "primary" : "default"}
        sx={{
          borderRadius: 1,
          color: active ? "#2065d1" : "#212B36",
          bgcolor: active ? "rgba(32, 101, 209, 0.12)" : "transparent",
          "&:hover": {
            bgcolor: active ? "rgba(32, 101, 209, 0.2)" : "rgba(145, 158, 171, 0.16)",
          },
          "&.Mui-disabled": {
            color: "rgba(33, 43, 54, 0.35)",
          },
          "& .MuiSvgIcon-root": {
            color: "inherit",
          },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const TipTapEditor = ({
  data = "",
  onChange,
  onBlur,
  readOnly = false,
  placeholder = "Description",
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const lastEmittedHtml = useRef(data || "");
  const [headingMenuAnchor, setHeadingMenuAnchor] = useState(null);
  const [fontMenuAnchor, setFontMenuAnchor] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: HEADING_LEVELS },
        // We configure Link ourselves below (custom target/rel) — leaving
        // StarterKit's default enabled too registers "link" twice.
        link: false,
      }),
      ResizableImage,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      FontFamily.configure({ types: ["textStyle"] }),
      Highlight.configure({ multicolor: true }),
      Subscript,
      Superscript,
    ],
    content: data || "",
    editable: !readOnly,
    immediatelyRender: false,
    // Tiptap v3 doesn't re-render the React tree on selection-only changes
    // by default (only on content changes) — without this, the toolbar's
    // active/highlighted state (bold, italic, etc.) keeps showing whatever
    // was active at the *previous* selection until something else forces
    // a re-render, e.g. typing.
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      lastEmittedHtml.current = html;
      onChange?.(html);
    },
    onBlur: () => {
      onBlur?.();
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor) return;
    const incoming = data || "";
    if (incoming === lastEmittedHtml.current) return;
    if (incoming === editor.getHTML()) return;
    lastEmittedHtml.current = incoming;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [data, editor]);

  const setImageAlign = useCallback(
    (positionClass) => {
      if (!editor) return;
      if (editor.isActive("image")) {
        const attrs = editor.getAttributes("image");
        const current = attrs.class || "";
        const cleaned = current
          .split(/\s+/)
          .filter(
            (c) =>
              c &&
              !c.startsWith("align-") &&
              !c.startsWith("float-") &&
              c !== "tiptap-image"
          )
          .join(" ");
        // "inline" means: flow with the text, no float/center class at all
        const nextClass = [cleaned, "tiptap-image", positionClass === "inline" ? null : positionClass]
          .filter(Boolean)
          .join(" ");

        const updates = { class: nextClass };
        // Side floats need a bounded width so text can wrap beside the image
        if (
          (positionClass === "float-left" || positionClass === "float-right") &&
          (!attrs.width || attrs.width > 420)
        ) {
          updates.width = 320;
        }
        if (positionClass === "align-center") {
          // Full-width block image — clear float
          updates.width = attrs.width || null;
        }

        editor.chain().focus().updateAttributes("image", updates).run();
      } else {
        const align = positionClass
          .replace("align-", "")
          .replace("float-", "");
        if (["left", "center", "right"].includes(align)) {
          editor.chain().focus().setTextAlign(align).run();
        }
      }
    },
    [editor]
  );

  const addLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL", previous || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const handleImageFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || !editor) return;

      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a valid image (JPEG, PNG, GIF, or WebP).");
        return;
      }

      setUploading(true);
      try {
        const res = await mediaUploadApi.uploadImage(file, "news");
        const url = resolveUploadedUrl(res?.data ?? res);
        if (!url) {
          toast.error("Image uploaded but no URL was returned.");
          return;
        }
        // Default to inline — drops the image right at the cursor, like
        // Word/Docs do for a freshly inserted image. The writer can switch
        // to a wrap/center layout afterwards from the toolbar.
        editor
          .chain()
          .focus()
          .setImage({ src: url })
          .updateAttributes("image", { class: "tiptap-image" })
          .run();
      } catch (err) {
        errorHandler(err);
      } finally {
        setUploading(false);
      }
    },
    [editor]
  );

  if (!editor) return null;

  return (
    <Box className={`tiptap-editor-root${readOnly ? " is-readonly" : ""}`}>
      {!readOnly && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
          className="tiptap-toolbar"
          sx={{ p: 1, borderBottom: 1, borderColor: "divider", gap: 0.25 }}
        >
          <ToolbarButton
            title="Paragraph & heading styles"
            onClick={(e) => setHeadingMenuAnchor(e.currentTarget)}
            active={editor.isActive("heading")}
          >
            <TitleIcon fontSize="small" />
            <ArrowDropDownIcon fontSize="small" sx={{ ml: -0.5 }} />
          </ToolbarButton>
          <Menu
            anchorEl={headingMenuAnchor}
            open={!!headingMenuAnchor}
            onClose={() => setHeadingMenuAnchor(null)}
          >
            <MenuItem
              selected={editor.isActive("paragraph")}
              onClick={() => {
                editor.chain().focus().setParagraph().run();
                setHeadingMenuAnchor(null);
              }}
            >
              Paragraph
            </MenuItem>
            {HEADING_LEVELS.map((level) => (
              <MenuItem
                key={level}
                selected={editor.isActive("heading", { level })}
                onClick={() => {
                  editor.chain().focus().toggleHeading({ level }).run();
                  setHeadingMenuAnchor(null);
                }}
              >
                Heading {level}
              </MenuItem>
            ))}
          </Menu>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <ToolbarButton
            title="Bold"
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
          >
            <FormatBoldIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive("italic")}
          >
            <FormatItalicIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Underline"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive("underline")}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Strikethrough"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            active={editor.isActive("strike")}
          >
            <StrikethroughSIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Inline code"
            onClick={() => editor.chain().focus().toggleCode().run()}
            active={editor.isActive("code")}
          >
            <CodeIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Subscript"
            onClick={() => editor.chain().focus().toggleSubscript().run()}
            active={editor.isActive("subscript")}
          >
            <SubscriptIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Superscript"
            onClick={() => editor.chain().focus().toggleSuperscript().run()}
            active={editor.isActive("superscript")}
          >
            <SuperscriptIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Blockquote"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            active={editor.isActive("blockquote")}
          >
            <FormatQuoteIcon fontSize="small" />
          </ToolbarButton>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <ToolbarButton
            title="Font family"
            onClick={(e) => setFontMenuAnchor(e.currentTarget)}
            active={!!editor.getAttributes("textStyle").fontFamily}
          >
            <FontDownloadOutlinedIcon fontSize="small" />
            <ArrowDropDownIcon fontSize="small" sx={{ ml: -0.5 }} />
          </ToolbarButton>
          <Menu
            anchorEl={fontMenuAnchor}
            open={!!fontMenuAnchor}
            onClose={() => setFontMenuAnchor(null)}
          >
            {FONT_FAMILIES.map((font) => (
              <MenuItem
                key={font.label}
                selected={editor.getAttributes("textStyle").fontFamily === font.value}
                sx={{ fontFamily: font.value || "inherit" }}
                onClick={() => {
                  if (font.value) {
                    editor.chain().focus().setFontFamily(font.value).run();
                  } else {
                    editor.chain().focus().unsetFontFamily().run();
                  }
                  setFontMenuAnchor(null);
                }}
              >
                {font.label}
              </MenuItem>
            ))}
          </Menu>
          <ColorSwatchButton
            title="Text color"
            icon={<FormatColorTextIcon fontSize="small" />}
            colors={TEXT_COLORS}
            activeColor={editor.getAttributes("textStyle").color}
            onPick={(color) => editor.chain().focus().setColor(color).run()}
            onClear={() => editor.chain().focus().unsetColor().run()}
          />
          <ColorSwatchButton
            title="Highlight color"
            icon={<FormatColorFillIcon fontSize="small" />}
            colors={HIGHLIGHT_COLORS}
            activeColor={editor.getAttributes("highlight").color}
            onPick={(color) => editor.chain().focus().toggleHighlight({ color }).run()}
            onClear={() => editor.chain().focus().unsetHighlight().run()}
          />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <ToolbarButton
            title="Bullet list"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
          >
            <FormatListBulletedIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Numbered list"
            onClick={() => editor.chain().focus().toggleNumberedList().run()}
            active={editor.isActive("orderedList")}
          >
            <FormatListNumberedIcon fontSize="small" />
          </ToolbarButton>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <ToolbarButton
            title="In line with text (drag the image to move it)"
            onClick={() => setImageAlign("inline")}
            active={
              editor.isActive("image") &&
              !/align-|float-/.test(String(editor.getAttributes("image").class || ""))
            }
          >
            <NotesIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Float image left (text wraps on right)"
            onClick={() => setImageAlign("float-left")}
            active={
              editor.isActive("image") &&
              String(editor.getAttributes("image").class || "").includes("float-left")
            }
          >
            <FormatAlignLeftIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Center image (full width block)"
            onClick={() => setImageAlign("align-center")}
            active={
              editor.isActive("image") &&
              String(editor.getAttributes("image").class || "").includes("align-center")
            }
          >
            <FormatAlignCenterIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Float image right (text wraps on left)"
            onClick={() => setImageAlign("float-right")}
            active={
              editor.isActive("image") &&
              String(editor.getAttributes("image").class || "").includes("float-right")
            }
          >
            <FormatAlignRightIcon fontSize="small" />
          </ToolbarButton>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <ToolbarButton title="Link" onClick={addLink} active={editor.isActive("link")}>
            <LinkIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Insert image"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? <CircularProgress size={18} /> : <ImageIcon fontSize="small" />}
          </ToolbarButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleImageFile}
          />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <ToolbarButton
            title="Undo"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <UndoIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Redo"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <RedoIcon fontSize="small" />
          </ToolbarButton>
        </Stack>
      )}
      <EditorContent editor={editor} className="tiptap-content" />
    </Box>
  );
};

export default TipTapEditor;
